import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { BAR_GRAPH_MAX_OPTIONS, BAR_GRAPH_QUESTIONS_PER_CHART_OPTIONS, BarGraphQuestionOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The three bar-graph dials (ADR 0039 §3, ADR 0040 §3-4).
 *
 * The operator bar is hidden for this format: a bar chart has a scale, not
 * two operands, and the question type already says what to do with it.
 *
 * Questions per chart sits here rather than behind "Advanced options": it
 * changes what the sheet *is* - one chart per question, or several questions
 * about one chart - not how it looks, and the format's own panel is basic
 * tier by definition - ADR 0023 §2 puts the format's own panel in the basic
 * tier whole, on the ground that splitting it would leave formats like place
 * value with a basic tier of nothing at all.
 */
export function BarGraphSettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();

    function handleQuestionChange(value: BarGraphQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...settings, BarGraphQuestion: value });
    }

    function handleMaxValueChange(event) {
        setSettings({ ...settings, BarGraphMaxValue: Number(event.target.value) });
    }

    function handleQuestionsPerChartChange(event) {
        setSettings({ ...settings, BarGraphQuestionsPerChart: Number(event.target.value) });
    }

    return (
        <div className="barGraphSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.BarGraphQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(BarGraphQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.chartScaleLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.chartScaleAria')}>
                {BAR_GRAPH_MAX_OPTIONS.map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`barGraphMaxValue-${option}`}
                            value={option}
                            checked={settings.BarGraphMaxValue === option}
                            onChange={handleMaxValueChange}
                        />
                        <label htmlFor={`barGraphMaxValue-${option}`}>{option}</label>
                    </div>
                ))}
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.questionsPerChartLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.questionsPerChartAria')}>
                {BAR_GRAPH_QUESTIONS_PER_CHART_OPTIONS.map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`barGraphQuestionsPerChart-${option}`}
                            value={option}
                            checked={settings.BarGraphQuestionsPerChart === option}
                            onChange={handleQuestionsPerChartChange}
                        />
                        <label htmlFor={`barGraphQuestionsPerChart-${option}`}>{option}</label>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default BarGraphSettings;
